import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'

import useClickOutside from '../../hooks/useClickOutside'
import Checkbox from './Checkbox'

// Reuse the same styles as MultiSelect to keep visual consistency
import './MultiSelectInput.scss'

export type TPropsSelectInput = {
  options: string[]
  selectedOption: string | null
  // eslint-disable-next-line no-unused-vars
  onSelectedOptionChange: (option: string | null) => void
  postfix?: string
  label?: string
  enableCustomOption?: boolean
  placeholder?: string
  error?: string
  info?: string
  required?: boolean
  flexibleWidth?: boolean
  parentStyle?: React.CSSProperties
}

const SelectInput = (props: TPropsSelectInput & React.InputHTMLAttributes<HTMLInputElement>) => {
  const {
    label,
    enableCustomOption = false,
    placeholder,
    required,
    options,
    selectedOption,
    onSelectedOptionChange,
    flexibleWidth,
    parentStyle
  } = props

  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (isOpen) {
      // Focus the search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }
  }, [isOpen])

  const handleClickOutside = () => {
    setSearchQuery('')
    setIsOpen(false)
  }

  useClickOutside(wrapperRef, handleClickOutside)

  const handleOptionClick = (option: string) => {
    if (selectedOption === option) {
      onSelectedOptionChange(null)
    } else {
      onSelectedOptionChange(option)
    }
    // Close dropdown after choosing in single-select
    setIsOpen(false)
    setSearchQuery('')
  }

  const renderedLabel = props.label ? (
    <label className="tq-input-label">
      {label}
      {required && <span className="tq-input-mandatory">*</span>}
    </label>
  ) : null

  return (
    <div className="tq-input-wrapper tq-multi-select" ref={wrapperRef} style={parentStyle}>
      {renderedLabel}
      <div className={clsx('tq-input-field', { 'flexible-width': flexibleWidth })} onClick={() => setIsOpen(!isOpen)}>
        <div className="tq-tag-container">
          {!selectedOption && <span className="placeholder">{placeholder}</span>}
          {selectedOption && <span>{selectedOption}</span>}
        </div>
        {props.postfix && <span className="tq-input-postfix">{props.postfix}</span>}
        <div className="chevron-down-icon" onClick={() => setIsOpen(!isOpen)}>
          <i className="fa fa-chevron-down" />
        </div>
      </div>
      {props.error && <span className="tq-input-error">{props.error}</span>}
      {props.info && <span className="tq-input-info">{props.info}</span>}
      {isOpen && (
        <div className="tq-input-options">
          <div className="tq-input-search-wrapper">
            <div className="tq-input-search-icon">
              <i className="fa fa-search" />
            </div>
            <input
              type="text"
              className="tq-input-search"
              ref={searchInputRef}
              placeholder={enableCustomOption ? 'Search or add new option' : 'Search'}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => {
                if (!enableCustomOption) return
                if (e.key === 'Enter') {
                  e.preventDefault()
                  const value = searchQuery.trim()
                  if (!value) return
                  const exactExists = options.includes(value)
                  if (!exactExists) {
                    if (selectedOption !== value) {
                      onSelectedOptionChange(value)
                    }
                    setSearchQuery('')
                  }
                }
              }}
              value={searchQuery}
            />
            {searchQuery && (
              <div className="close-icon" onClick={() => setSearchQuery('')}>
                <i className="fa fa-times" />
              </div>
            )}
          </div>
          {options
            .filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()))
            .map(option => (
              <div
                className={clsx('tq-input-option', { selected: selectedOption === option })}
                key={option}
                onClick={() => handleOptionClick(option)}
              >
                {selectedOption === option && <Checkbox label={option} checked />}
                {selectedOption !== option && <span className="tq-input-option-label">{option}</span>}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}

export default SelectInput
